// Editor-facing language service: position queries over a SemanticIndex.
//
// Everything here is Monaco-agnostic and works in plain source offsets, so it is
// straightforward to test and equally usable from a web worker. The playground's
// Monaco providers are thin adapters over these methods.

import { parseRecover } from "./parser.js";
import { analyze, defId, DefInfo, Diagnostic, Member, SemanticIndex, typeMembers, typeStr } from "./checker.js";
import { BUILTINS, containerTypeParams } from "./stdlib.js";
import * as ast from "./ast.js";
import { isGenericContainer } from "./ast.js";
import { Type } from "./types.js";
import { Span } from "./token.js";

export interface Completion {
  label: string;
  detail: string;
  kind: "keyword" | "function" | "struct" | "enum" | "variant" | "field" | "variable" | "method";
  /** Whether accepting this completion should open its call — a function, method,
   *  or constructor is invoked with `(...)`. The editor adapters use it to insert
   *  the parentheses and pop up signature help so the argument list is shown right
   *  away (see `signatureHelpAt`). A plain value / keyword / type name is not. */
  callable: boolean;
}

/** One parameter within a `SignatureInfo`, for the editor to highlight as the
 *  cursor moves between arguments. `label` is the parameter's text as it appears
 *  in the signature (`key: string`); `range` are its `[start, end)` offsets within
 *  `SignatureInfo.label`, so the host can highlight the exact span even when the
 *  same text occurs more than once. */
export interface SignatureParam {
  label: string;
  range: [number, number];
}

/** One callable's signature — the one-line form shown in hovers (`fn set(self,
 *  key: string, value: int)`), split into the parameters the caller supplies.
 *  A method's `self` is part of `label` but never a `param` (it isn't an argument
 *  the caller passes), so `params` lines up one-to-one with the call's arguments. */
export interface SignatureInfo {
  label: string;
  params: SignatureParam[];
}

/** Parameter hints for the call the cursor is inside — the popup that shows which
 *  argument comes next. Modeled on LSP `SignatureHelp`: one or more overloads
 *  (STM has no overloading, so always exactly one) plus which one and which
 *  parameter are active. `activeParam` indexes `signatures[activeSignature].params`,
 *  or is null when the callee takes no arguments. */
export interface SignatureHelp {
  signatures: SignatureInfo[];
  activeSignature: number;
  activeParam: number | null;
}

export interface Hover {
  markdown: string;
  span: Span;
}

/** One inlay hint — a small label the editor renders inline (not part of the
 *  buffer) at `offset`. Two kinds are produced: a function's *inferred* effect set
 *  shown after its return type (`fn f(a: int) int ! retry`), and a binding's
 *  *inferred* type shown after its name (`x: int = 5`). The host maps `offset` to
 *  its own position type and `kind` to its inlay-hint category; `paddingLeft` /
 *  `paddingRight` request a space on that side so the label doesn't abut the code. */
export interface InlayHint {
  offset: number;
  label: string;
  kind: "type" | "effect";
  paddingLeft: boolean;
  paddingRight: boolean;
}

/** The kind of a `DocumentSymbol`, mapped by each editor host to its own symbol
 *  enum (VS Code's `SymbolKind`, Monaco's `languages.SymbolKind`). Covers exactly
 *  the top-level declaration shapes the outline surfaces. */
export type SymbolKind = "struct" | "field" | "enum" | "variant" | "function" | "method" | "trait" | "impl";

/** One node of the file's symbol outline: a declaration and, for the container
 *  kinds, its members. Mirrors the LSP `DocumentSymbol` shape so the editor
 *  adapters are one-to-one — `span` is the whole declaration (what the editor
 *  reveals), `selectionSpan` the narrower name to select (always inside `span`),
 *  and `children` the nested members (a struct's fields, an enum's variants, a
 *  trait's / impl's methods). */
export interface DocumentSymbol {
  name: string;
  detail: string;
  kind: SymbolKind;
  span: Span;
  selectionSpan: Span;
  children: DocumentSymbol[];
}

const KEYWORDS = [
  "struct",
  "enum",
  "trait",
  "impl",
  "fn",
  "let",
  "return",
  "if",
  "else",
  "while",
  "for",
  "in",
  "match",
  "go",
  "txn",
  "assert",
  "true",
  "false",
];

const KIND_OF: Record<DefInfo["kind"], Completion["kind"]> = {
  struct: "struct",
  enum: "enum",
  trait: "struct",
  variant: "variant",
  fn: "function",
  method: "method",
  field: "field",
  param: "variable",
  let: "variable",
  "loop-var": "variable",
  binding: "variable",
  builtin: "function",
};

/** Parse (with error recovery) + analyze a source string into a queryable
 *  language service. Never throws: a syntax error drops the offending
 *  statement but keeps the rest, so hovers, definitions, and completions all
 *  work across an in-progress edit. */
export function analyzeSource(src: string): LanguageService {
  const { program, errors } = parseRecover(src);
  const parseDiags: Diagnostic[] = errors.map((e) => ({
    severity: "error",
    message: e.message,
    span: e.span,
  }));
  // The AST is threaded in alongside the index: hovers over a generic call read
  // the checker's inferred type arguments (elaboration, keyed by AST node), and
  // the node under the cursor is found by walking the tree, not the index. The
  // source text is kept too: signature help counts the top-level commas between
  // the call's `(` and the cursor to find the active argument.
  return new LanguageService(analyze(program), program, src, parseDiags);
}

function width(span: Span): number {
  return span.end.offset - span.start.offset;
}
function hasLocation(span: Span): boolean {
  return width(span) > 0 || span.start.offset > 0;
}
/** Whether `span` covers `offset` *within the same file*. A query carries the
 *  file it fires in (`source`): `undefined` = the user's document, a non-empty id
 *  = a bundled stdlib file. Offsets only mean anything within one file, so both
 *  the offset range and the source must match — a stdlib span never answers a
 *  query in the user's buffer, and vice versa.
 *
 *  The interval is half-open (`[start, end)`): the offset *after* a token's last
 *  character belongs to whatever comes next, not to the token. `inclusiveEnd`
 *  widens it to `[start, end]` so the end boundary counts too — used as a
 *  fallback by navigation, where the cursor placed immediately after an
 *  identifier's last character should still resolve to that identifier. */
function coversIn(span: Span, offset: number, source: string | undefined, inclusiveEnd = false): boolean {
  return (
    (span.source ?? undefined) === (source ?? undefined) &&
    span.start.offset <= offset &&
    (inclusiveEnd ? offset <= span.end.offset : offset < span.end.offset)
  );
}

/** Render a type expression as written in source (`Set<Player>`, `(int) -> int`),
 *  for the impl-header label in the outline. The checker's `typeStr` renders a
 *  resolved `Type`; this renders the surface `TypeExpr` before resolution, so an
 *  impl over an unknown/partial target still gets a faithful label. */
function typeExprStr(t: ast.TypeExpr): string {
  if (t.kind === "fn") return `(${t.params.map(typeExprStr).join(", ")}) -> ${typeExprStr(t.ret)}`;
  return t.args.length ? `${t.name}<${t.args.map(typeExprStr).join(", ")}>` : t.name;
}

/** The outline label for an `impl` block — `impl Show for Circle`, or `impl Circle`
 *  for an inherent impl (no trait). An impl has no identifier of its own, so this
 *  reconstructs the header from the target and (optional) trait. */
function implLabel(d: ast.ImplDecl): string {
  const target = typeExprStr(d.target);
  if (!d.trait) return `impl ${target}`;
  const args = d.traitArgs.length ? `<${d.traitArgs.map(typeExprStr).join(", ")}>` : "";
  return `impl ${d.trait}${args} for ${target}`;
}

function symbolNode(
  name: string,
  detail: string,
  kind: SymbolKind,
  span: Span,
  selectionSpan: Span,
  children: DocumentSymbol[] = []
): DocumentSymbol {
  return { name, detail, kind, span, selectionSpan, children };
}

/** The span of just a call's callee *name* — the identifier of a free
 *  function/constructor, or the method name of a `recv.method(...)` call — which is
 *  what a hover on a generic call should light up (not the whole call). */
function calleeSpan(call: ast.CallExpr): Span {
  return call.callee.kind === "field" ? call.callee.fieldSpan : call.callee.span;
}

/** Visit every expression node in the program (pre-order). There is no general AST
 *  visitor in the repo, so this is a small hand-rolled walk over exactly the
 *  statement and expression shapes the grammar produces; unrecognized kinds are
 *  simply not recursed into, so it never throws on partial/unexpected trees. The
 *  position lookups (`forEachCall`, `operatorAt`) filter its output by node kind. */
function forEachExpr(program: ast.Program, visit: (e: ast.Expr) => void): void {
  walkNodes(program, { onExpr: visit });
}

/** The single hand-rolled AST walk. `onExpr`/`onStmt` fire pre-order on every
 *  expression / statement node; the position lookups filter by node kind. There is
 *  no general visitor in the repo, so this covers exactly the shapes the grammar
 *  produces; unrecognized kinds aren't recursed into, so it never throws on a
 *  partial/unexpected tree. */
function walkNodes(
  program: ast.Program,
  visit: { onExpr?: (e: ast.Expr) => void; onStmt?: (s: ast.Stmt) => void }
): void {
  const expr = (e: ast.Expr | null | undefined): void => {
    if (!e) return;
    visit.onExpr?.(e);
    switch (e.kind) {
      case "call":
        expr(e.callee);
        for (const a of e.args) expr(a.value);
        break;
      case "binary":
        expr(e.left);
        expr(e.right);
        break;
      case "unary":
        expr(e.operand);
        break;
      case "field":
        expr(e.object);
        break;
      case "lambda":
        if (e.body.kind === "block") block(e.body);
        else expr(e.body);
        break;
      case "ifExpr":
        expr(e.cond);
        expr(e.then);
        expr(e.else);
        break;
      case "list":
        for (const el of e.elements) expr(el);
        break;
      case "brace":
        for (const en of e.entries) {
          if (en.key) expr(en.key);
          expr(en.value);
        }
        break;
      // literals and idents carry no nested expressions
    }
  };
  const block = (b: ast.Block): void => {
    for (const s of b.stmts) stmt(s);
  };
  const stmt = (s: ast.Stmt): void => {
    visit.onStmt?.(s);
    switch (s.kind) {
      case "let":
        expr(s.value);
        break;
      case "assign":
        expr(s.target);
        expr(s.value);
        break;
      case "exprStmt":
        expr(s.expr);
        break;
      case "return":
        expr(s.value);
        break;
      case "if":
        expr(s.cond);
        block(s.then);
        if (s.else) {
          if (s.else.kind === "if") stmt(s.else);
          else block(s.else);
        }
        break;
      case "while":
        expr(s.cond);
        block(s.body);
        break;
      case "for":
        expr(s.iterable);
        block(s.body);
        break;
      case "go":
        expr(s.call);
        break;
      case "txn":
        block(s.body);
        break;
      case "raise":
        expr(s.value);
        expr(s.cause);
        break;
      case "try":
        block(s.body);
        block(s.handler.body);
        break;
      case "match":
        expr(s.subject);
        for (const arm of s.arms) block(arm.body);
        break;
      case "assert":
        expr(s.cond);
        expr(s.message);
        break;
      case "block":
        block(s);
        break;
      // retry: no sub-expressions
    }
  };
  for (const d of program.decls) {
    if (d.kind === "fn") block(d.body);
    else if (d.kind === "impl") for (const m of d.methods) block(m.body);
    else if (d.kind === "trait") for (const m of d.methods) if (m.body) block(m.body);
  }
}

/** Visit every `for` statement in the program (a filter over the node walk). */
function forEachForStmt(program: ast.Program, visit: (f: ast.ForStmt) => void): void {
  walkNodes(program, {
    onStmt: (s) => {
      if (s.kind === "for") visit(s);
    },
  });
}

/** The clickable span of a `for x in c` loop's `in` — the gap between the loop
 *  variable and the iterable, where the `in` keyword sits. Like a binary operator,
 *  the `for` node records no keyword span, and nested loops have disjoint gaps. */
function inGap(f: ast.ForStmt): Span {
  return { start: f.varSpan.end, end: f.iterable.span.start, source: f.span.source };
}

/** Visit every `CallExpr` in the program (a filter over `forEachExpr`). */
function forEachCall(program: ast.Program, visit: (call: ast.CallExpr) => void): void {
  forEachExpr(program, (e) => {
    if (e.kind === "call") visit(e);
  });
}

/** The clickable span of a binary operator: the gap between its operands, where the
 *  operator token (and any surrounding whitespace) sits. `BinaryExpr` records no
 *  operator span of its own, but nested binaries have disjoint gaps
 *  (`a + b + c` → the outer gap is after `b`, the inner between `a` and `b`), so the
 *  narrowest gap covering an offset identifies the operator under the cursor. */
function operatorGap(e: ast.BinaryExpr): Span {
  return { start: e.left.span.end, end: e.right.span.start, source: e.span.source };
}

/** Whether a completion of this kind names something you call — offered with its
 *  parentheses opened and signature help popped up on accept. */
function isCallable(kind: Completion["kind"]): boolean {
  return kind === "function" || kind === "method";
}

/** The `[start, end)` offset ranges of each top-level parameter in a one-line
 *  signature — the slices of `label` between its outermost `(` and matching `)`,
 *  split on the commas at paren-nesting depth 0. Works uniformly over every
 *  signature shape the checker renders (`fn add(a: int, b: int) int`, a method's
 *  `fn set(self, key: string, value: int)`, a variant's `Shape.Circle(radius:
 *  float)`), because they all share that surface form. Nested commas — a generic
 *  argument (`Dict<int, string>`) or a function-typed parameter (`f: (int) -> int`)
 *  — sit at depth > 0 and don't split; a `>` in a return arrow (`->`) at depth 0 is
 *  ignored. A signature with no `(` (a bare `struct Point`) yields no params. */
function parseParamRanges(label: string): [number, number][] {
  const open = label.indexOf("(");
  if (open < 0) return [];
  const ranges: [number, number][] = [];
  const push = (s: number, e: number) => {
    while (s < e && label[s] === " ") s++;
    while (e > s && label[e - 1] === " ") e--;
    if (e > s) ranges.push([s, e]);
  };
  let depth = 0;
  let segStart = open + 1;
  for (let i = open + 1; i < label.length; i++) {
    const c = label[i]!;
    if (c === "(" || c === "<" || c === "[" || c === "{") depth++;
    else if (c === ")" || c === "]" || c === "}") {
      if (depth === 0) {
        push(segStart, i);
        return ranges;
      }
      depth--;
    } else if (c === ">") {
      if (depth > 0) depth--; // a bracketed generic close; a stray `->` arrow is a no-op
    } else if (c === "," && depth === 0) {
      push(segStart, i);
      segStart = i + 1;
    }
  }
  // Unterminated (a signature cut off mid-render) — take what's left.
  push(segStart, label.length);
  return ranges;
}

/** Split a one-line signature into a `SignatureInfo`. `skipSelf` drops the leading
 *  `self` parameter of a method's signature, so the remaining `params` line up with
 *  the arguments the caller actually writes. */
function signatureFromLabel(label: string, skipSelf: boolean): SignatureInfo {
  const ranges = parseParamRanges(label);
  const params = (skipSelf ? ranges.slice(1) : ranges).map(([s, e]) => ({
    label: label.slice(s, e),
    range: [s, e] as [number, number],
  }));
  return { label, params };
}

/** The name a type is keyed by in the member tables (struct / enum / container /
 *  trait), or null for a primitive — which never carries a user operator overload. */
function typeName(t: Type): string | null {
  switch (t.k) {
    case "struct":
    case "enum":
    case "struct-generic":
    case "enum-generic":
    case "generic":
    case "trait":
      return t.name;
    default:
      return null;
  }
}

export class LanguageService {
  constructor(
    private readonly index: SemanticIndex,
    private readonly program: ast.Program,
    private readonly src: string,
    private readonly parseDiags: Diagnostic[] = []
  ) {}

  /** Whether the source parsed with no syntax errors. */
  get parsed(): boolean {
    return this.parseDiags.length === 0;
  }

  /** Diagnostics for markers: syntax errors when present (type-checking a
   *  broken parse would cascade), otherwise the type checker's findings. A
   *  finding located in a prelude file (a `source`-tagged span) is dropped: the
   *  prelude is assumed well-formed, and its offsets don't index the user's
   *  buffer, so it must never surface as a marker on their document. */
  get diagnostics(): Diagnostic[] {
    return (this.parseDiags.length ? this.parseDiags : this.index.diagnostics).filter((d) => !d.span.source);
  }

  /** Type / signature to show when hovering the token at `offset` in `source`
   *  (`undefined` = the user's document, a file id = a bundled stdlib tab). */
  hoverAt(offset: number, source: string | undefined = undefined): Hover | null {
    // On an overloaded operator, show the trait method it dispatches to (`Add.add`);
    // on a loop's `in`, the `iter()`/`next()` method it drives the iterable through —
    // each with the method's signature, the same resolution go-to-definition jumps to.
    // A native operator (no overload) falls through to the plain result-type hover.
    // Both walk the user program's AST, so they apply to the user buffer only; a query
    // in a stdlib tab uses the ordinary lookup below.
    if (source === undefined) {
      const bin = this.operatorAt(offset);
      const opTarget = bin ? this.operatorTarget(bin) : null;
      if (opTarget) {
        const text = `${opTarget.trait}.${opTarget.method.name}\n${opTarget.method.detail}`;
        return { markdown: "```stm\n" + text + "\n```", span: operatorGap(bin!) };
      }
      const loop = this.forLoopAt(offset);
      const iterTarget = loop ? this.iterableTarget(loop) : null;
      if (iterTarget) {
        const text = `${iterTarget.label}\n${iterTarget.method.detail}`;
        return { markdown: "```stm\n" + text + "\n```", span: inGap(loop!) };
      }
    }
    let best: { span: Span; def: DefInfo | null; type: Type } | null = null;
    const consider = (span: Span, def: DefInfo | null, type: Type) => {
      if (!coversIn(span, offset, source)) return;
      if (!best || width(span) < width(best.span)) best = { span, def, type };
    };
    for (const r of this.index.refs) consider(r.span, r.def, r.type);
    for (const n of this.index.nodeTypes) consider(n.span, null, n.type);
    if (!best) return null;
    const b = best as { span: Span; def: DefInfo | null; type: Type };
    const body = b.def ? b.def.detail : typeStr(b.type);
    // When the cursor is on a generic call's callee, prepend the concrete
    // instantiation (e.g. `id<int>`) above the signature. It's the one thing the
    // plain def/type hover can't show — the type arguments were inferred per call
    // site and live only in the elaboration — so it goes first, inside the fence.
    const call = this.genericCallAt(offset, source);
    const inst = call ? this.instantiationLabel(call) : null;
    const text = inst ? inst + "\n" + body : body;
    return { markdown: "```stm\n" + text + "\n```", span: b.span };
  }

  /** The definition span to jump to for the reference at `offset`, if any. The
   *  returned span's `source`, when set, names the bundled stdlib file the target
   *  lives in (e.g. a builtin like `print` or a prelude type like `Option`);
   *  `undefined` means the current document. The editor adapter maps `source` to
   *  the right file — a real file in the VS Code extension, a virtual model in the
   *  playground — instead of assuming the current buffer. */
  definitionAt(offset: number, source: string | undefined = undefined): Span | null {
    // cursor, follow it to the definition it resolves to. Works from a use in the
    // user's buffer or inside a stdlib tab (`source` names the file the cursor is in).
    const use = this.useDefAt(offset, source)?.nameSpan;
    if (use) return use;
    // An overloaded operator (`a + b`), or a `for x in c` loop's `in`, has no
    // reference edge — each dispatches through a trait method the checker recorded in
    // the elaboration. Jump to that impl. Both walk the program AST, so this applies to
    // the user's buffer only (source === undefined), not a stdlib tab.
    if (source === undefined) {
      const bin = this.operatorAt(offset);
      const opTarget = bin ? this.operatorTarget(bin) : null;
      if (opTarget && hasLocation(opTarget.method.nameSpan)) return opTarget.method.nameSpan;
      const loop = this.forLoopAt(offset);
      const iterTarget = loop ? this.iterableTarget(loop) : null;
      if (iterTarget && hasLocation(iterTarget.method.nameSpan)) return iterTarget.method.nameSpan;
    }
    return null;
  }

  /** Every use of the symbol at `offset` in `source`, for "find all usages".
   *  Works whether the cursor sits on a use or on the defining identifier itself,
   *  and from either the user's buffer or a stdlib tab (`source` names the file
   *  the cursor is in). When `includeDeclaration` (the default), the declaration
   *  is listed among the results, as editors expect from "find all references".
   *
   *  This is the other end of the same reference edges go-to-definition walks:
   *  having found the definition, we read its incoming edges out of the semantic
   *  index's reference graph. Results span every file — asking for the usages of a
   *  builtin like `print` returns both the user's call sites and the library's own
   *  internal calls, each carrying the `source` of the file it lives in so the
   *  editor can open it. Returns [] when `offset` isn't on a symbol. */
  referencesAt(offset: number, includeDeclaration = true, source: string | undefined = undefined): Span[] {
    const def = this.symbolDefAt(offset, source);
    if (!def) return [];
    const out: Span[] = [];
    const seen = new Set<string>();
    const push = (span: Span) => {
      if (!hasLocation(span)) return;
      const key = `${span.source ?? ""}:${span.start.offset}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(span);
    };
    if (includeDeclaration) push(def.nameSpan);
    for (const span of this.index.referenceGraph.uses.get(defId(def.nameSpan)) ?? []) push(span);
    return out;
  }

  /** Completions for a bare identifier position: locals, globals, builtins, keywords. */
  identifierCompletions(offset: number): Completion[] {
    const out: Completion[] = [];
    const seen = new Set<string>();
    const add = (label: string, detail: string, kind: Completion["kind"]) => {
      if (seen.has(label)) return;
      seen.add(label);
      out.push({ label, detail, kind, callable: isCallable(kind) });
    };
    for (const def of this.scopeDefsAt(offset)) add(def.name, def.detail, KIND_OF[def.kind]);
    for (const def of this.index.globals) add(def.name, def.detail, KIND_OF[def.kind]);
    for (const [name, b] of Object.entries(BUILTINS)) add(name, b.sig, "function");
    for (const kw of KEYWORDS) add(kw, "keyword", "keyword");
    return out;
  }

  /** Completions after a `.`: the members of the receiver path's type. Reads the
   *  same `typeMembers` the checker uses, so completion never drifts from what
   *  actually type-checks — a field completes as a field, a method as a method. */
  memberCompletions(path: string[], offset: number): Completion[] {
    const type = this.typeOfPath(path, offset);
    if (!type) return [];
    return typeMembers(
      type,
      (n) => this.index.structFields.get(n) ?? [],
      (n) => this.index.typeMethods.get(n) ?? [],
      (t) => this.declaredTypeParams(t)
    ).map((m) => ({
      label: m.name,
      detail: m.detail,
      kind: m.params ? ("method" as const) : ("field" as const),
      callable: !!m.params,
    }));
  }

  /** Parameter hints for the call the cursor sits inside — the popup that shows the
   *  callee's signature and highlights the argument being typed. Finds the innermost
   *  call whose parentheses cover `offset`, resolves its callee through the same
   *  elaboration the checker recorded (so a method's parameters are its receiver's
   *  substituted types, a `Dict<string, int>`'s `set` reading `key: string, value:
   *  int`), and counts the top-level commas before the cursor to mark the active
   *  argument. Confined to the user's buffer — the call walk is over the parsed
   *  document, not a stdlib tab — so a query in a stdlib `source` returns null. */
  signatureHelpAt(offset: number, source: string | undefined = undefined): SignatureHelp | null {
    if (source !== undefined) return null;
    const found = this.callAt(offset);
    if (!found) return null;
    const info = this.calleeSignature(found.call);
    if (!info) return null;
    const activeParam = this.activeParamAt(found.call, found.open, offset, info.params.length);
    return { signatures: [info], activeSignature: 0, activeParam };
  }

  /** A generic type's declared parameter names, so member completions specialize to
   *  the instance's arguments (mirrors the checker's `declaredTypeParams`): a
   *  struct/enum/trait's own params from the index, a container's from
   *  `containerTypeParams`. */
  private declaredTypeParams(type: Type): string[] | undefined {
    if (type.k === "generic") return isGenericContainer(type.name) ? containerTypeParams(type.name) : undefined;
    if (type.k === "struct-generic" || type.k === "enum-generic" || type.k === "trait")
      return this.index.typeParams.get(type.name);
    return undefined;
  }

  /** A hierarchical outline of the file's declarations — structs (with their
   *  fields), enums (with their variants), traits and impls (with their methods),
   *  and free functions — for the editor's outline view, breadcrumbs, and symbol
   *  search. In declaration order. Confined to the user's buffer: the program
   *  walked here is the parsed document, never the prelude. */
  documentSymbols(): DocumentSymbol[] {
    const out: DocumentSymbol[] = [];
    for (const d of this.program.decls) {
      const sym = this.declSymbol(d);
      if (sym) out.push(sym);
    }
    return out;
  }

  /** Inlay hints for the document: a function's *inferred* effect set after its
   *  return type (`fn f(a: int) int ! retry`), and a binding's *inferred* type
   *  after its name (`x: int = 5`). Only inferred facts are shown — a function that
   *  already writes a `! …` annotation, or a `let`/binding with an explicit type,
   *  says so in the source and gets no hint. Confined to the user's buffer: the
   *  walked program is the parsed document, never the prelude. */
  inlayHints(): InlayHint[] {
    const out: InlayHint[] = [];

    // Effect hints, keyed by the checker's callable keys (`fn:<name>`,
    // `method:<owner>.<name>`). Free functions, impl methods (where `retry`/`io`
    // actually surface — `Future.get`, `Chan.recv`), and bodied trait defaults.
    for (const d of this.program.decls) {
      if (d.kind === "fn") this.effectHint(d, `fn:${d.name}`, out);
      else if (d.kind === "impl")
        for (const m of d.methods) this.effectHint(m, `method:${d.typeName}.${m.name}`, out);
      else if (d.kind === "trait")
        for (const m of d.methods) this.effectHint(m, `method:${d.name}.${m.name}`, out);
    }

    // Type hints on `let`/declaring-assignment bindings that write no type. Every
    // binding's `DefInfo` is keyed by the offset of its *defining* name; a
    // reassignment's target is not a defining name, so it has no entry and gets no
    // hint — exactly the filter we want (only declaration sites are annotated).
    const bindingByOffset = new Map<number, DefInfo>();
    for (const s of this.index.scopes)
      for (const def of s.defs)
        if ((def.nameSpan.source ?? undefined) === undefined) bindingByOffset.set(def.nameSpan.start.offset, def);

    walkNodes(this.program, {
      onStmt: (s) => {
        if (s.kind === "let" && s.type === null && !this.obviousType(s.value))
          this.typeHint(s.nameSpan, bindingByOffset, out);
        else if (s.kind === "assign" && s.target.kind === "ident" && !this.obviousType(s.value))
          this.typeHint(s.target.span, bindingByOffset, out);
      },
    });

    return out;
  }

  /** Whether a binding's initializer already makes its type plain, so a `: T` hint
   *  would only echo the right-hand side: a scalar or collection *literal*, or a
   *  *constructor* call (`Point(…)`, `Some(…)`) whose type is named in the syntax.
   *  A negated numeric literal (`-5`) counts; anything else (a call, a field access,
   *  an operator result) does not, and keeps its hint. */
  private obviousType(value: ast.Expr): boolean {
    switch (value.kind) {
      case "int":
      case "float":
      case "string":
      case "char":
      case "bool":
      case "list":
      case "brace":
        return true;
      case "unary":
        return this.obviousType(value.operand);
      case "call":
        return this.index.elaboration.calls.get(value)?.kind === "constructor";
      default:
        return false;
    }
  }

  /** Emit an effect hint for one callable, or nothing. Skipped when the effects
   *  are *declared* rather than inferred — a `builtin`/abstract method (no body),
   *  or a signature that already carries a `! …` annotation — and when the inferred
   *  set is empty (pure). The hint sits after the return type, or (for a void
   *  return) just before the body's opening brace, so it reads where a written
   *  annotation would. */
  private effectHint(
    fn: { effects?: ast.EffectName[]; builtin?: boolean; returnType: ast.TypeExpr | null; body?: ast.Block },
    key: string,
    out: InlayHint[]
  ): void {
    if (fn.builtin || !fn.body) return;
    if (fn.effects && fn.effects.length > 0) return;
    const effects = this.index.effects.get(key);
    if (!effects || effects.length === 0) return;
    const label = `! ${effects.join(" ")}`;
    if (fn.returnType)
      out.push({ offset: fn.returnType.span.end.offset, label, kind: "effect", paddingLeft: true, paddingRight: false });
    else out.push({ offset: fn.body.span.start.offset, label, kind: "effect", paddingLeft: false, paddingRight: true });
  }

  /** Emit a `: <type>` hint after a binding name, or nothing. The name must be a
   *  *declaring* occurrence (its own `DefInfo` is keyed by this exact span) with a
   *  known type — a reassignment has no entry here, and an `unknown` type is noise. */
  private typeHint(nameSpan: Span, bindingByOffset: Map<number, DefInfo>, out: InlayHint[]): void {
    if ((nameSpan.source ?? undefined) !== undefined) return;
    const def = bindingByOffset.get(nameSpan.start.offset);
    if (!def || def.nameSpan.start.offset !== nameSpan.start.offset) return;
    if (def.type.k === "unknown") return;
    out.push({
      offset: nameSpan.end.offset,
      label: `: ${typeStr(def.type)}`,
      kind: "type",
      paddingLeft: false,
      paddingRight: false,
    });
  }

  /** The outline node for one top-level declaration, or null when its defining
   *  name has no usable location (a shape error recovery left without one). Detail
   *  strings are read from the same reference-graph defs the hovers use, so the
   *  outline never drifts from the signatures shown elsewhere. */
  private declSymbol(d: ast.Decl): DocumentSymbol | null {
    const members = (ms: { name: string; nameSpan: Span; span: Span }[]): DocumentSymbol[] =>
      ms
        .filter((m) => hasLocation(m.nameSpan))
        .map((m) => symbolNode(m.name, this.symbolDetail(m.nameSpan, m.name), "method", m.span, m.nameSpan));

    switch (d.kind) {
      case "struct": {
        if (!hasLocation(d.nameSpan)) return null;
        const fields = d.fields
          .filter((f) => hasLocation(f.nameSpan))
          .map((f) => symbolNode(f.name, this.symbolDetail(f.nameSpan, `${d.name}.${f.name}`), "field", f.span, f.nameSpan));
        return symbolNode(d.name, this.symbolDetail(d.nameSpan, `struct ${d.name}`), "struct", d.span, d.nameSpan, fields);
      }
      case "enum": {
        if (!hasLocation(d.nameSpan)) return null;
        const variants = d.variants
          .filter((v) => hasLocation(v.nameSpan))
          .map((v) => symbolNode(v.name, this.symbolDetail(v.nameSpan, v.name), "variant", v.span, v.nameSpan));
        return symbolNode(d.name, this.symbolDetail(d.nameSpan, `enum ${d.name}`), "enum", d.span, d.nameSpan, variants);
      }
      case "fn": {
        if (!hasLocation(d.nameSpan)) return null;
        return symbolNode(d.name, this.symbolDetail(d.nameSpan, `fn ${d.name}`), "function", d.span, d.nameSpan);
      }
      case "trait": {
        if (!hasLocation(d.nameSpan)) return null;
        return symbolNode(d.name, this.symbolDetail(d.nameSpan, `trait ${d.name}`), "trait", d.span, d.nameSpan, members(d.methods));
      }
      case "impl": {
        // An impl has no name of its own; label it from the header and select the
        // target type's name (always inside the block's span).
        const selection = hasLocation(d.typeSpan) ? d.typeSpan : d.span;
        return symbolNode(implLabel(d), "", "impl", d.span, selection, members(d.methods));
      }
    }
  }

  /** The one-line signature to show for a declaration in the outline: the same
   *  `detail` the hovers use, read from the reference graph by the name's location,
   *  falling back to `fallback` when the def wasn't indexed. */
  private symbolDetail(nameSpan: Span, fallback: string): string {
    return this.index.referenceGraph.defs.get(defId(nameSpan))?.detail ?? fallback;
  }

  // -- internals ----------------------------------------------------------

  /** The generic call whose *callee name* the cursor sits on, or null. A call is
   *  generic iff the checker recorded a non-empty `typeArgs` entry for it; among
   *  those covering `offset`, the narrowest callee wins, so `inner` in
   *  `outer(inner(3))` resolves to the inner call rather than the enclosing one.
   *  Never throws: an absent elaboration entry just means "not generic here". */
  private genericCallAt(offset: number, source: string | undefined): ast.CallExpr | null {
    const typeArgs = this.index.elaboration.typeArgs;
    let best: ast.CallExpr | null = null;
    forEachCall(this.program, (call) => {
      const args = typeArgs.get(call);
      if (!args || args.length === 0) return; // non-generic call: nothing to instantiate
      const span = calleeSpan(call);
      if (!coversIn(span, offset, source)) return;
      if (!best || width(span) < width(calleeSpan(best))) best = call;
    });
    return best;
  }

  /** The innermost call whose argument parentheses cover `offset`, and the offset of
   *  its opening `(` — what signature help describes. A call's `(` is the first one
   *  after the callee (and past any explicit type arguments, so `Set<int>()` isn't
   *  fooled by a paren inside the `<…>`); the argument region runs from there to the
   *  closing paren at the call's span end. Among covering calls the narrowest wins,
   *  so in `outer(inner(3))` the cursor resolves to the call it is really inside. */
  private callAt(offset: number): { call: ast.CallExpr; open: number } | null {
    let best: { call: ast.CallExpr; open: number; width: number } | null = null;
    forEachCall(this.program, (call) => {
      if (call.span.source) return; // user buffer only
      let from = call.callee.span.end.offset;
      for (const ta of call.typeArgs) from = Math.max(from, ta.span.end.offset);
      const open = this.src.indexOf("(", from);
      if (open < 0 || open >= call.span.end.offset) return;
      if (offset <= open || offset >= call.span.end.offset) return;
      const w = call.span.end.offset - open;
      if (!best || w < best.width) best = { call, open, width: w };
    });
    if (!best) return null;
    const b = best as { call: ast.CallExpr; open: number; width: number };
    return { call: b.call, open: b.open };
  }

  /** The signature to show for a call, resolved through the elaboration's record of
   *  what the callee is. A method reads its receiver's substituted members (concrete
   *  parameter types); a free function, builtin, and enum variant read the one-line
   *  signature already in their `detail`; a struct constructor is built from its
   *  field types. A call to a function-typed value has no parameter names to show. */
  private calleeSignature(call: ast.CallExpr): SignatureInfo | null {
    const res = this.index.elaboration.calls.get(call);
    if (!res) return null;
    switch (res.kind) {
      case "method": {
        const member = this.methodMemberAt(res.receiver, res.method);
        return member ? signatureFromLabel(member.detail, true) : null;
      }
      case "fn": {
        const def = this.index.globals.find((d) => d.name === res.name && d.kind === "fn");
        return def ? signatureFromLabel(def.detail, false) : null;
      }
      case "builtin": {
        const b = BUILTINS[res.name];
        return b ? signatureFromLabel(b.sig, false) : null;
      }
      case "constructor": {
        if (this.index.structFields.has(res.name)) return this.structConstructorSignature(call, res.name);
        const def = this.index.globals.find((d) => d.name === res.name && d.kind === "variant");
        return def ? signatureFromLabel(def.detail, false) : null;
      }
      default:
        return null; // fn-value / unknown
    }
  }

  /** The callable `Member` for `receiver.name`, read through the same `typeMembers`
   *  the checker and completion use — so its parameter types are the receiver's
   *  arguments substituted in (a `Dict<string, int>`'s `set`, not `K`/`V`). */
  private methodMemberAt(receiver: Type, name: string): Member | null {
    const members = typeMembers(
      receiver,
      (n) => this.index.structFields.get(n) ?? [],
      (n) => this.index.typeMethods.get(n) ?? [],
      (t) => this.declaredTypeParams(t)
    );
    return members.find((m) => m.name === name && m.params) ?? null;
  }

  /** A struct constructor's signature, built from the constructed instance's fields
   *  — the struct's own `detail` is just `struct Point`, carrying no argument list.
   *  Reading the fields off the *result* type (`elaboration.types`) means a generic
   *  `Pair<int, string>(…)` shows concrete field types, since `typeMembers`
   *  substitutes the instance's arguments. */
  private structConstructorSignature(call: ast.CallExpr, name: string): SignatureInfo | null {
    const type = this.index.elaboration.types.get(call) ?? ({ k: "struct", name } as Type);
    const fields = typeMembers(
      type,
      (n) => this.index.structFields.get(n) ?? [],
      (n) => this.index.typeMethods.get(n) ?? [],
      (t) => this.declaredTypeParams(t)
    ).filter((m) => !m.params); // fields only, not the struct's impl methods
    const label = `${name}(${fields.map((f) => `${f.name}: ${typeStr(f.type)}`).join(", ")})`;
    return signatureFromLabel(label, false);
  }

  /** Which argument slot the cursor is in: the number of top-level commas between
   *  the opening `(` and `offset`, clamped to the last parameter. A comma nested
   *  inside an argument (a nested call, a generic type argument, a collection) sits
   *  within that argument's span and doesn't advance the slot. Null when the callee
   *  takes no arguments (nothing to highlight). */
  private activeParamAt(call: ast.CallExpr, open: number, offset: number, paramCount: number): number | null {
    if (paramCount === 0) return null;
    let commas = 0;
    for (let p = open + 1; p < offset && p < this.src.length; p++) {
      if (this.src[p] === "," && this.commaSeparatesArgs(call, p)) commas++;
    }
    return Math.min(commas, paramCount - 1);
  }

  /** Whether the comma at `p` separates two of the call's arguments (a top-level
   *  comma), rather than sitting inside one argument's own expression. */
  private commaSeparatesArgs(call: ast.CallExpr, p: number): boolean {
    for (const a of call.args) {
      if (a.value.span.start.offset <= p && p < a.value.span.end.offset) return false;
      if (a.nameSpan && a.nameSpan.start.offset <= p && p < a.nameSpan.end.offset) return false;
    }
    return true;
  }

  /** The binary operator whose token the cursor is on, or null. Matches the gap
   *  between operands (see `operatorGap`); the narrowest such gap wins, so in
   *  `a + b + c` the cursor lands on the operator it's actually over. */
  private operatorAt(offset: number): ast.BinaryExpr | null {
    let best: ast.BinaryExpr | null = null;
    forEachExpr(this.program, (e) => {
      if (e.kind !== "binary") return;
      const gap = operatorGap(e);
      if (!coversIn(gap, offset, undefined)) return;
      if (!best || width(gap) < width(operatorGap(best))) best = e;
    });
    return best;
  }

  /** The trait method a user overloaded operator dispatches to — `a + b` on a `V`
   *  resolves to `V`'s `Add.add` impl — read from the elaboration's operator decision
   *  and looked up among the receiver type's members. Null for a native operator (no
   *  overload) or when the receiver carries no such method. */
  private operatorTarget(bin: ast.BinaryExpr): { trait: string; method: DefInfo } | null {
    const res = this.index.elaboration.binaries.get(bin);
    if (res?.kind !== "overload") return null;
    const name = typeName(res.receiver);
    if (!name) return null;
    const method = (this.index.typeMethods.get(name) ?? []).find((m) => m.name === res.method);
    return method ? { trait: res.trait, method } : null;
  }

  /** The `for` loop whose `in` keyword the cursor is on, or null (narrowest gap). */
  private forLoopAt(offset: number): ast.ForStmt | null {
    let best: ast.ForStmt | null = null;
    forEachForStmt(this.program, (f) => {
      const gap = inGap(f);
      if (!coversIn(gap, offset, undefined)) return;
      if (!best || width(gap) < width(inGap(best))) best = f;
    });
    return best;
  }

  /** The method a `for x in c` loop drives its iterable through — `iter()` for an
   *  `Iterable`, `next()` for an `Iterator` — read from the elaboration's iteration
   *  plan and looked up among the iterable type's members. This is what "go to the
   *  iterable implementation" jumps to. Null when the iteration plan is unknown or the
   *  method isn't on the recorded type. */
  private iterableTarget(f: ast.ForStmt): { label: string; method: DefInfo } | null {
    const res = this.index.elaboration.loops.get(f);
    if (!res || res.kind === "unknown") return null;
    const name = res.kind === "container" ? res.container : res.typeName;
    const methodName = res.kind === "iterator" ? "next" : "iter";
    const method = (this.index.typeMethods.get(name) ?? []).find((m) => m.name === methodName);
    return method ? { label: `${name}.${methodName}`, method } : null;
  }

  /** The `name<arg1, arg2>` instantiation to show for a generic call, or null when
   *  there's nothing worth showing: no recorded callee name, or inference failed on
   *  *every* argument (an all-`<unknown>` annotation is just noise). Reads the
   *  inferred type arguments straight from the elaboration and renders them with the
   *  same `typeStr` the checker's hovers use — a lone `unknown` still renders. */
  private instantiationLabel(call: ast.CallExpr): string | null {
    const args = this.index.elaboration.typeArgs.get(call);
    if (!args || args.length === 0) return null;
    const res = this.index.elaboration.calls.get(call);
    const name = res?.kind === "method" ? res.method : res && "name" in res ? res.name : null;
    if (!name) return null;
    if (args.every((t) => t.k === "unknown")) return null;
    return `${name}<${args.map(typeStr).join(", ")}>`;
  }

  /** The definition node the cursor is on, or null — the graph node both
   *  navigation queries pivot on. The cursor may be on a *use* (resolve it to its
   *  definition, narrowest ref winning) or on the *declaration* itself, so "find
   *  usages" fires from either. Only occurrences in the current document locate the
   *  cursor; a prelude span's offsets index a different file and could collide
   *  numerically. */
  private symbolDefAt(offset: number, source: string | undefined): DefInfo | null {
    const onUse = this.useDefAt(offset, source);
    if (onUse) return onUse;
    let decl: DefInfo | null = null;
    for (const def of this.index.referenceGraph.defs.values()) {
      if (!hasLocation(def.nameSpan) || !coversIn(def.nameSpan, offset, source)) continue;
      if (!decl || width(def.nameSpan) < width(decl.nameSpan)) decl = def;
    }
    return decl;
  }

  /** If a *use* covers `offset`, the definition it resolves to (narrowest use
   *  wins), else null. Only uses in the user's buffer locate the cursor; a
   *  prelude-tagged use span could collide numerically with a low offset in the
   *  user's document. Shared by `definitionAt` and `symbolDefAt`.
   *
   *  A use that *strictly* covers the offset always wins; only when none does do
   *  we accept one that *ends* exactly at the offset. That fallback handles the
   *  cursor sitting immediately after an identifier's last character (`count|`),
   *  which editors treat as still being on the word, without ever letting a
   *  token that merely ends at a boundary override the token that starts there. */
  private useDefAt(offset: number, source: string | undefined): DefInfo | null {
    return this.bestUseAt(offset, source, false)?.def ?? this.bestUseAt(offset, source, true)?.def ?? null;
  }

  /** The narrowest use covering `offset`, or null. With `inclusiveEnd`, a use
   *  ending exactly at `offset` counts as covering it (see `useDefAt`). */
  private bestUseAt(offset: number, source: string | undefined, inclusiveEnd: boolean): { span: Span; def: DefInfo } | null {
    let best: { span: Span; def: DefInfo } | null = null;
    for (const r of this.index.refs) {
      if (!r.def || !coversIn(r.span, offset, source, inclusiveEnd) || !hasLocation(r.def.nameSpan)) continue;
      if (!best || width(r.span) < width(best.span)) best = { span: r.span, def: r.def };
    }
    return best;
  }

  /** Bindings visible at `offset`, innermost scope first (shadowing outer). */
  private scopeDefsAt(offset: number): DefInfo[] {
    const scopes = this.index.scopes
      .filter((s) => coversIn(s.span, offset, undefined))
      .sort((a, b) => width(a.span) - width(b.span));
    const out: DefInfo[] = [];
    const seen = new Set<string>();
    for (const s of scopes) {
      for (const def of s.defs) {
        if (seen.has(def.name)) continue;
        seen.add(def.name);
        out.push(def);
      }
    }
    return out;
  }

  private lookupName(name: string, offset: number): DefInfo | undefined {
    for (const def of this.scopeDefsAt(offset)) if (def.name === name) return def;
    return this.index.globals.find((d) => d.name === name);
  }

  /** Resolve a dotted receiver path (e.g. `["g", "players"]`) to a type. */
  private typeOfPath(path: string[], offset: number): Type | null {
    if (path.length === 0) return null;
    let def = this.lookupName(path[0]!, offset);
    if (!def) return null;
    let type = def.type;
    for (let i = 1; i < path.length; i++) {
      if (type.k !== "struct" && type.k !== "struct-generic") return null;
      const field = (this.index.structFields.get(type.name) ?? []).find((f) => f.name === path[i]);
      if (!field) return null;
      // For a generic instantiation, the field type may be a type variable; the
      // hover shows the parameter name (best-effort, no substitution here).
      type = field.type;
    }
    return type;
  }
}
